import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle2, AlertCircle } from 'lucide-react';

export default function Contact() {
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="pt-28">
      {/* Content Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20">
            <div className="space-y-12">
              {/* Page Title & Intro */}
              <div>
                <h1 className="text-4xl md:text-6xl font-bold text-black mb-8 tracking-tight uppercase">
                  Kontakt <span className="text-[#C1121F]">L1</span> i dag
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Har du brug for hjælp til el-installationer, service eller entrepriseopgaver? Kontakt os for en uforpligtende snak om dit projekt. Vi står klar til at rådgive dig om den bedste løsning.
                </p>
              </div>

              <div className="text-black">
                <h2 className="text-xl font-bold text-black mb-6 uppercase tracking-wider">Send os en besked</h2>
                <div className="mt-12 mb-16 prose prose-p:text-black prose-p:text-xl prose-p:leading-relaxed prose-p:mb-6 max-w-none">
                  <p>
                    Vi bestræber os på at besvare alle henvendelser inden for 24 timer på hverdage. Uanset om det drejer sig om en mindre reparation eller et større byggeprojekt, er vi klar til at hjælpe.
                  </p>
                </div>
                
                <div className="bg-white border border-gray-200 p-8 md:p-10 rounded-none shadow-sm">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot */}
                    <div className="sr-only absolute -z-10">
                      <label htmlFor="website_url">Website URL</label>
                      <input type="text" id="website_url" name="website_url" tabIndex={-1} autoComplete="off" />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label htmlFor="name" className="text-sm font-bold text-gray-500 uppercase tracking-widest">Navn</label>
                        <input
                          required
                          type="text"
                          id="name"
                          name="name"
                          className="w-full bg-gray-50 border border-gray-200 rounded-none px-4 py-4 text-black focus:outline-none focus:border-[#C1121F] transition-colors"
                          placeholder="Dit fulde navn"
                        />
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="email" className="text-sm font-bold text-gray-500 uppercase tracking-widest">Email</label>
                        <input
                          required
                          type="email"
                          id="email"
                          name="email"
                          className="w-full bg-gray-50 border border-gray-200 rounded-none px-4 py-4 text-black focus:outline-none focus:border-[#C1121F] transition-colors"
                          placeholder="din@email.dk"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-sm font-bold text-gray-500 uppercase tracking-widest">Telefon</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full bg-gray-50 border border-gray-200 rounded-none px-4 py-4 text-black focus:outline-none focus:border-[#C1121F] transition-colors"
                        placeholder="Dit telefonnummer"
                      />
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="message" className="text-sm font-bold text-gray-500 uppercase tracking-widest">Besked</label>
                      <textarea
                        required
                        id="message"
                        name="message"
                        rows={6}
                        className="w-full bg-gray-50 border border-gray-200 rounded-none px-4 py-4 text-black focus:outline-none focus:border-[#C1121F] transition-colors resize-none"
                        placeholder="Hvordan kan vi hjælpe dig?"
                      />
                    </div>

                    <button
                      disabled={status === 'loading'}
                      type="submit"
                      className="w-full py-5 bg-[#C1121F] text-white font-bold uppercase tracking-wider rounded-none hover:bg-[#A00F1A] transition-all flex items-center justify-center space-x-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {status === 'loading' ? (
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <span>Send besked</span>
                          <Send className="w-5 h-5" />
                        </>
                      )}
                    </button>

                    {status === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-none flex items-center space-x-3 text-green-600">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-bold">Tak for din besked! Vi vender tilbage hurtigst muligt.</span>
                      </div>
                    )}

                    {status === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-none flex items-center space-x-3 text-red-600">
                        <AlertCircle className="w-5 h-5" />
                        <span className="font-bold">Der skete en fejl. Prøv venligst igen eller ring til os.</span>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="sticky top-40">
                <div className="p-8 bg-white border border-gray-200 rounded-none shadow-sm">
                  <h3 className="text-xl font-bold text-black mb-8 uppercase tracking-wider">Kontaktoplysninger</h3>
                  <div className="space-y-8">
                    <div className="flex items-start space-x-4">
                      <div className="w-5 flex justify-center shrink-0 pt-1">
                        <Phone className="w-5 h-5 text-black" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 uppercase tracking-widest font-bold mb-1">Telefon</div>
                        <a href="tel:+4522518070" className="text-xl font-bold text-black hover:text-[#C1121F] transition-colors">22 51 80 70</a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-5 flex justify-center shrink-0 pt-1">
                        <Mail className="w-5 h-5 text-black" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 uppercase tracking-widest font-bold mb-1">Mail</div>
                        <a href="mailto:L12026@outlook.dk" className="text-lg font-bold text-black hover:text-[#C1121F] transition-colors break-all">L12026@outlook.dk</a>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4">
                      <div className="w-5 flex justify-center shrink-0 pt-1">
                        <MapPin className="w-5 h-5 text-black" />
                      </div>
                      <div>
                        <div className="text-sm text-gray-500 uppercase tracking-widest font-bold mb-1">Adresse</div>
                        <div className="text-lg font-bold text-black">Danmark</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 p-8 bg-[#C1121F] rounded-none">
                  <h3 className="text-xl font-bold text-white mb-4 uppercase tracking-wider">Virksomhedsinfo</h3>
                  <div className="space-y-2 text-white/80 font-medium">
                    <p>L1 El-installatør</p>
                    <p>CVR: 46200373</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
